import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import {
  Box,
  Button,
  Grid,
  InputAdornment,
  Table,
  TableBody,
  TextField,
} from '@mui/material';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';

import { Helmet } from 'react-helmet-async';

import { yupResolver } from '@hookform/resolvers/yup';
import { updatePasswordSchema } from '../../../../validation/schemas/updatePasswordSchema';

import api from '../../../../services/api';
import FormStyle from '../FormStyle';
import showTableData from '../Functions/showTableData';

function ChangePasswordConfirmation(
  {
    setIsEditing,
    setChangedPasswordSucessfully,
  }: {
    setIsEditing: (isEditing: boolean) => void,
    setChangedPasswordSucessfully: (changedPasswordSucessfully: boolean) => void,
  },
) {
  // variables
  const [currentPasswordError, setCurrentPasswordError] = useState('');
  const [isShowingPassword, setIsShowingPassword] = useState<Array<boolean>>([false, false, false]);

  // form
  const {
    register: addFormValues,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    mode: 'onSubmit',
    resolver: yupResolver(updatePasswordSchema),
    criteriaMode: 'all',
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      newPasswordConfirm: '',
    },
  });

  // functions
  const handleChangePasswordConfirmation = handleSubmit(async (data) => {
    setCurrentPasswordError('');
    api.post('/v1/users/change-password', data)
      .then((response) => {
        const { changedSuccessfully } = response.data;
        if (changedSuccessfully) {
          reset();
          setChangedPasswordSucessfully(true);
          setIsEditing(false);
        }
      }).catch((error) => {
        setCurrentPasswordError(error.response.data.message);
      });
  });

  const handleShowOrHidePassword = (event: React.MouseEvent) => {
    switch (event.currentTarget.id) {
      case '1':
        setIsShowingPassword([!isShowingPassword[0], isShowingPassword[1], isShowingPassword[2]]);
        break;
      case '2':
        setIsShowingPassword([isShowingPassword[0], !isShowingPassword[1], isShowingPassword[2]]);
        break;
      case '3':
        setIsShowingPassword([isShowingPassword[0], isShowingPassword[1], !isShowingPassword[2]]);
        break;
      default:
        setIsShowingPassword([false, false, false]);
        break;
    }
  };

  return (
    <div className="main">
      <Helmet>
        <title>パスワード変更</title>
      </Helmet>
      <main
        style={{
          overflowY: 'auto',
          height: 'calc(100vh - 65px)',
        }}
      >
        <Box className="container-fluid my-1">
          <Box sx={FormStyle.previewHeader}>
            <h1 className="text-bold">パスワード変更</h1>
            現在のパスワードと新しいパスワードをご入力いただき、「保存する」ボタンを押してください。
          </Box>
          <Box className="card">
            <Box className="card-body">
              <Grid
                container
                justifyContent="center"
                spacing={1}
              >
                <Grid
                  item
                  xs={12}
                >
                  <Table className="table-form-setting">
                    <TableBody>
                      {
                        showTableData({
                          ticket: '現在のパスワード',
                          value: (
                            <TextField
                              {...addFormValues('currentPassword')}
                              type={isShowingPassword[0] ? 'text' : 'password'}
                              sx={{ width: '100%' }}
                              size="small"
                              placeholder="現在のパスワード"
                              error={!!currentPasswordError}
                              helperText={currentPasswordError}
                              InputProps={{
                                endAdornment:
                                  <InputAdornment
                                    id="1"
                                    onClick={handleShowOrHidePassword}
                                    position="end"
                                  >
                                    <FontAwesomeIcon icon={isShowingPassword[0] ? faEyeSlash : faEye} />
                                  </InputAdornment>,
                              }}
                            />
                          ),
                          isRequired: true,
                        })
                      }
                      {
                        showTableData({
                          ticket: '新しいパスワード',
                          value: (
                            <TextField
                              {...addFormValues('newPassword')}
                              type={isShowingPassword[1] ? 'text' : 'password'}
                              sx={{ width: '100%' }}
                              size="small"
                              placeholder="新しいパスワード"
                              helperText={errors.newPassword?.message}
                              error={!!errors.newPassword}
                              InputProps={{
                                endAdornment:
                                  <InputAdornment
                                    id="2"
                                    onClick={handleShowOrHidePassword}
                                    position="end"
                                  >
                                    <FontAwesomeIcon icon={isShowingPassword[1] ? faEyeSlash : faEye} />
                                  </InputAdornment>,
                              }}
                            />
                          ),
                          isRequired: true,
                          extraText: '半角英数字混在で8文字以上',
                        })
                      }
                      {
                        showTableData({
                          ticket: '新しいパスワード確認',
                          value: (
                            <TextField
                              {...addFormValues('newPasswordConfirm')}
                              type={isShowingPassword[2] ? 'text' : 'password'}
                              sx={{ width: '100%' }}
                              size="small"
                              placeholder="新しいパスワード（確認）"
                              helperText={errors.newPasswordConfirm?.message}
                              error={!!errors.newPasswordConfirm}
                              InputProps={{
                                endAdornment:
                                  <InputAdornment
                                    id="3"
                                    onClick={handleShowOrHidePassword}
                                    position="end"
                                  >
                                    <FontAwesomeIcon icon={isShowingPassword[2] ? faEyeSlash : faEye} />
                                  </InputAdornment>,
                              }}
                            />
                          ),
                          isRequired: true,
                        })
                      }
                    </TableBody>
                  </Table>
                </Grid>
                <Grid
                  item
                >
                  <Button
                    onClick={() => { setIsEditing(false); }}
                    variant="outlined"
                    className="btn-outline-secondary"
                  >
                    戻る
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    onClick={handleChangePasswordConfirmation}
                    className="btn-primary"
                  >
                    保存する
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Box>
      </main>
    </div>
  );
}

export default ChangePasswordConfirmation;
